
import React, { useEffect, useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, Legend, PieChart, Pie
} from 'recharts';

// If you don't have Vite envs, hardcode your API base here
const API_BASE ='https://gantasoft.in/cab-g/dev/admin';

// Minimal Card wrappers (no external UI lib)
function Card({ children }) { return <div className="bg-white rounded-xl shadow-sm">{children}</div>; }
function CardContent({ children, className }) { return <div className={`p-4 ${className||''}`}>{children}</div>; }

export default function StatsDashboard(){
  const [from, setFrom] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0,10));
  const [to, setTo] = useState(() => new Date().toISOString().slice(0,10));
  const [driverId, setDriverId] = useState('');
  const [dailySubscription, setDailySubscription] = useState('0');
  const [commissionRate, setCommissionRate] = useState('0.20');
  const [commissionAbove, setCommissionAbove] = useState('0');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchStats = async () => {
    setLoading(true); setError('');
    try {
      const qs = new URLSearchParams({ from, to, tz: 'Asia/Kolkata' });
      if (driverId) qs.append('driver_id', driverId);
      if (dailySubscription) qs.append('daily_subscription', dailySubscription);
      if (commissionRate) qs.append('commission_rate', commissionRate);
      if (commissionAbove) qs.append('commission_above', commissionAbove);

      const res = await fetch(`${API_BASE}/stats.php?${qs.toString()}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Failed');
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchStats(); }, []);

  const kpi = data?.totals || {};
  const vehicleSeries = useMemo(() => (data?.byVehicle || []).map(v => ({
    name: v.vehicle_type || 'N/A',
    fare: Number(v.fare),
    tips: Number(v.tips),
    gross: Number(v.gross),
    rides: Number(v.rides)
  })), [data]);
  const payModes = useMemo(() => (data?.paymentModes || []).map(p => ({ name: p.payment_mode, amount: Number(p.amount) })), [data]);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3 bg-white rounded-xl shadow-sm p-4">
        <div className="col-span-1">
          <label className="text-xs font-semibold">From</label>
          <input type="date" className="border rounded-md px-2 py-1 w-full" value={from} onChange={e=>setFrom(e.target.value)} />
        </div>
        <div className="col-span-1">
          <label className="text-xs font-semibold">To</label>
          <input type="date" className="border rounded-md px-2 py-1 w-full" value={to} onChange={e=>setTo(e.target.value)} />
        </div>
        <div className="col-span-1">
          <label className="text-xs font-semibold">Driver ID (optional)</label>
          <input className="border rounded-md px-2 py-1 w-full" value={driverId} onChange={e=>setDriverId(e.target.value)} placeholder="e.g. 42" />
        </div>
        <div className="col-span-1">
          <label className="text-xs font-semibold">Daily Subscription (₹)</label>
          <input className="border rounded-md px-2 py-1 w-full" value={dailySubscription} onChange={e=>setDailySubscription(e.target.value)} />
        </div>
        <div className="col-span-1">
          <label className="text-xs font-semibold">Commission Rate</label>
          <input className="border rounded-md px-2 py-1 w-full" value={commissionRate} onChange={e=>setCommissionRate(e.target.value)} placeholder="0.20" />
        </div>
        <div className="col-span-1">
          <label className="text-xs font-semibold">Commission Above (₹)</label>
          <input className="border rounded-md px-2 py-1 w-full" value={commissionAbove} onChange={e=>setCommissionAbove(e.target.value)} placeholder="same as subscription" />
        </div>
        <div className="col-span-1 md:col-span-6 flex gap-3">
          <button onClick={fetchStats} className="px-3 py-2 rounded-md bg-black text-white">Apply</button>
          {loading && <span className="mt-2 text-sm">Loading...</span>}
          {error && <span className="mt-2 text-sm text-red-600">{error}</span>}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card><CardContent><div className="text-xs text-gray-500">Rides</div><div className="text-2xl font-bold">{kpi.rides || 0}</div></CardContent></Card>
        <Card><CardContent><div className="text-xs text-gray-500">Completed</div><div className="text-2xl font-bold">{kpi.completed || 0}</div></CardContent></Card>
        <Card><CardContent><div className="text-xs text-gray-500">Cancelled</div><div className="text-2xl font-bold">{kpi.cancelled || 0}</div></CardContent></Card>
        <Card><CardContent><div className="text-xs text-gray-500">Distance (km)</div><div className="text-2xl font-bold">{kpi.distance_km || 0}</div></CardContent></Card>
        <Card><CardContent><div className="text-xs text-gray-500">Fare (₹)</div><div className="text-2xl font-bold">{kpi.fare_total || 0}</div></CardContent></Card>
        <Card><CardContent><div className="text-xs text-gray-500">Tips (₹)</div><div className="text-2xl font-bold">{kpi.tip_total || 0}</div></CardContent></Card>
      </div>

      {/* Line: By Day Gross */}
      <Card>
        <CardContent>
          <div className="font-semibold mb-2">Daily Gross</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.byDay || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="d" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="gross" name="Gross (₹)" />
                <Line type="monotone" dataKey="fare" name="Fare (₹)" />
                <Line type="monotone" dataKey="tips" name="Tips (₹)" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Bar: Vehicle breakdown */}
      <Card>
        <CardContent>
          <div className="font-semibold mb-2">By Vehicle Type</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={vehicleSeries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="gross" name="Gross (₹)" />
                <Bar dataKey="rides" name="Rides" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Pie: Payment modes */}
      <Card>
        <CardContent>
          <div className="font-semibold mb-2">Payment Modes</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Tooltip />
                <Legend />
                <Pie data={payModes} dataKey="amount" nameKey="name" outerRadius={110} label />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Driver payout */}
      {data?.payout && (
        <Card>
          <CardContent>
            <div className="font-semibold mb-2">Driver Payout (Net)</div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-gray-500">Daily Subscription</div>
                <div className="font-bold">₹{Number(data.payout.daily_subscription).toFixed(2)}</div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-gray-500">Commission Rate</div>
                <div className="font-bold">{Number(data.payout.commission_rate*100).toFixed(0)}%</div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-gray-500">Commission Above</div>
                <div className="font-bold">₹{Number(data.payout.commission_above).toFixed(2)}</div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-gray-500">Subscription Total</div>
                <div className="font-bold">₹{Number(data.payout.subscription_fee_total).toFixed(2)}</div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-gray-500">Commission</div>
                <div className="font-bold">₹{Number(data.payout.commission_total).toFixed(2)}</div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-gray-500">Net Driver Earnings</div>
                <div className="font-bold">₹{Number(data.payout.net_driver_earnings).toFixed(2)}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}